import { useEffect, type ReactNode } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useSignedIn } from '../../session.tsx'
import { useFx, useStores } from '../../lib/groupData.ts'
import { useTripSync } from '../../lib/tripSync.ts'
import { useDesktop } from '../../lib/useDesktop.ts'
import { DeskToolbar } from '../../components/desk.tsx'
import { Button, EmptyState, Paragraph, ScreenHeader, Spinner } from '../../components/ui.tsx'
import type { TripViewProps } from './shared.ts'
import { PickerView } from './PickerView.tsx'
import { TripView } from './TripView.tsx'
import { PrintView } from './PrintView.tsx'

/**
 * Jedna routa /nakup/:id/* pro planovani, vyber ze sablony i nakupovani.
 * Synchronizace tak zustava namountovana pri prepinani rezimu a fronta
 * zmen se neposila ze dvou mist.
 */
export function TripScreen() {
  const { tripId = '' } = useParams<{ tripId: string }>()
  // Jiny nakup = cista synchronizace; bez klice zustal po prechodu v bocnim panelu na obrazovce predchozi nakup.
  return <TripScreenFor key={tripId} tripId={tripId} />
}

function TripScreenFor({ tripId }: { tripId: string }) {
  const { user, group, groups, selectGroup } = useSignedIn()
  const location = useLocation()
  const sync = useTripSync(tripId, user.id)
  const groupId = sync.trip?.groupId ?? null
  const stores = useStores(groupId, true)
  const fx = useFx()

  // Nakup z jine skupiny (treba odkaz v Messengeru) prepne aktualni skupinu.
  useEffect(() => {
    if (groupId && groupId !== group.id && groups.some((g) => g.id === groupId)) selectGroup(groupId)
  }, [groupId, group.id, groups, selectGroup])

  if (sync.missing) {
    return (
      <Frame>
        <EmptyState title="Nákup nenalezen" action={<BackHome />}>
          Někdo ho mezitím smazal, nebo patří skupině, ve které nejsi.
        </EmptyState>
      </Frame>
    )
  }

  if (!sync.trip) {
    return (
      <Frame>
        {sync.offline ? (
          <Paragraph>Nákup není uložený v tomhle zařízení a není signál.</Paragraph>
        ) : sync.error ? (
          <Paragraph tone="error">{sync.error}</Paragraph>
        ) : (
          <Spinner />
        )}
      </Frame>
    )
  }

  const props: TripViewProps = {
    trip: sync.trip,
    sync,
    stores: stores.res.state === 'ok' ? stores.res.data : [],
    fx: fx.res.state === 'ok' ? fx.res.data : null,
  }
  const rest = location.pathname.slice(`/nakup/${tripId}`.length)
  if (rest.startsWith('/tisk')) return <PrintView {...props} />
  // Ze sablony jde pridavat i do bezneho nakupu (grilovacka na jeden obchod).
  if (rest.startsWith('/sablona')) return <PickerView {...props} />
  return <TripView {...props} forceShop={rest.startsWith('/obchod')} />
}

function BackHome() {
  const navigate = useNavigate()
  return <Button onClick={() => navigate('/')}>Zpět na nákupy</Button>
}

function Frame({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const desktop = useDesktop()
  if (desktop) {
    return (
      <div className="desk-view-main desk-narrow">
        <DeskToolbar title="Nákup" />
        {children}
      </div>
    )
  }
  return (
    <div className="app-shell">
      <div className="page">
        <ScreenHeader title="Nákup" onBack={() => navigate('/')} />
        <main className="page-content">{children}</main>
      </div>
    </div>
  )
}
